import * as React from "react";
import { Button, Spinner } from "@fluentui/react-components";
import {
  speEditingService,
  type OfficeOperations,
} from "../../services/speEditingService";
import {
  DocumentWriteError,
  documentWriteMessage,
} from "../../services/documentWriteError";
const money = (value: string | number | null | undefined) =>
  value === null || value === undefined
    ? "noch nicht ermittelt"
    : `${(Number(value) / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const date = (value: string | null) =>
  value ? new Date(value).toLocaleString("de-DE") : "noch nicht ermittelt";
export const OfficeOperationsSection: React.FC = () => {
  const [data, setData] = React.useState<OfficeOperations | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      setData(await speEditingService.operations());
    } catch (e) {
      setError(
        e instanceof DocumentWriteError && e.status === 403
          ? "Für diese Betriebsansicht fehlt die ausdrückliche Betriebsberechtigung."
          : "Die Betriebsansicht konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => {
    void load();
  }, [load]);
  return (
    <section aria-label="Office-Betrieb">
      <h2>Office-Betrieb</h2>
      <p>
        Diese globale Betriebsansicht erfordert eine gesonderte
        Betriebsberechtigung zusätzlich zur Administratorrolle.
      </p>
      <Button disabled={loading} onClick={() => void load()}>
        Aktualisieren
      </Button>
      {loading && <Spinner label="Betriebsstatus wird geladen…" />}
      {error && <p role="alert">{error}</p>}
      {data && (
        <>
          <p>
            Monatsbudget: {money(data.monthlyBudgetMinor)} · Warnschwelle:{" "}
            {money(data.warnAtMinor)} — Kosten sperren nichts, sie werden den
            Betreibern nur gemeldet.
          </p>
          <p>Azure-Istkosten: {money(data.actualAzureCostMinor)}</p>
          <p>Letzter Azure-Abgleich: {date(data.lastAzureReconciledAt)}</p>
          <p>
            Lokale Verbrauchsschätzung: {money(data.estimatedConsumptionMinor)}{" "}
            · Reserviert: {money(data.reservedCostsMinor)}
          </p>
          <p>
            Konservative Prognose: {money(data.forecastMonthEndMinor)} –
            Azure-Istkosten plus lokale Schätzung und rollierende
            30-Tage-Reserve. Dies ist keine Vertragsabrechnung.
          </p>
          <p>
            {data.activeContainers} aktive Arbeitskopien ·{" "}
            {data.unknownUsageContainers} ohne aktuelle Verbrauchsmessung ·{" "}
            {data.importBacklog} Sicherungen ausstehend · {data.cleanupBacklog}{" "}
            Aufräumvorgänge ausstehend
          </p>
          <p>
            Aufrufe: {data.graphCalls} · davon Sicherungs-/Abschlussaufrufe:{" "}
            {data.recoveryGraphCalls} · Aktive Bytes: {data.activeBytes} ·
            Gelöschte Bytes: {data.deletedBytes ?? "noch nicht ermittelt"} ·
            Übertragene Bytes: {data.egressBytes}
          </p>
          <p>Letzte erfolgreiche Sicherung: {date(data.lastSuccessfulSync)}</p>
          <p>
            Standortprüfung:{" "}
            {data.residencyStatus === "deferred"
              ? "zurückgestellt"
              : data.residencyStatus === "verified"
                ? "geprüft"
                : "noch nicht geprüft"}
            . Aufbewahrungssperren: Anbieter-Schutz, unabhängig ungeprüft.
          </p>
          {data.costAlerts.length > 0 && (
            <ul aria-label="Kostenhinweise">
              {data.costAlerts.map((alert) => (
                <li key={alert.kind}>
                  <strong>{alert.title}</strong> — {alert.body}
                </li>
              ))}
            </ul>
          )}
          <h3>Mandanten</h3>
          <ul>
            {data.tenants.map((t) => (
              <li key={t.tenantId}>
                {t.tenantId}: {t.activeContainers} Arbeitskopien,{" "}
                {t.activeBytes} Bytes, {t.cleanupBacklog} Aufräumvorgänge
              </li>
            ))}
          </ul>
          <h3>Arbeitskopien</h3>
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Mandant / Dokument</th>
                  <th>Status</th>
                  <th>Letzte Sicherung</th>
                  <th>Hinweis</th>
                  <th>Wiederholung</th>
                </tr>
              </thead>
              <tbody>
                {data.workspaces.map((w) => (
                  <tr key={w.workspaceId}>
                    <td>
                      {w.tenantId} / {w.documentId}
                    </td>
                    <td>{w.state}</td>
                    <td>{date(w.lastSavedAt)}</td>
                    <td>
                      {w.reason
                        ? documentWriteMessage(
                            w.reason,
                            "Prüfung durch Betrieb erforderlich",
                          )
                        : "–"}
                      {w.usageUnknown ? " · Verbrauch unbekannt" : ""}
                    </td>
                    <td>
                      {date(w.retryAt)} · {w.attempts ?? "noch nicht ermittelt"}{" "}
                      Versuche
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};
